import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Box, Flex, HStack, IconButton, Tooltip } from '@chakra-ui/react';
import type { Identifier, XYCoord } from 'dnd-core';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
    MdDesktopWindows,
    MdDragIndicator,
    MdOutlineDesktopAccessDisabled,
} from 'react-icons/md';
import {
    TbBrowserCheck,
    TbBrowserOff,
    TbDeviceMobile,
    TbDeviceMobileOff,
    TbDeviceTablet,
    TbDeviceTabletOff,
} from 'react-icons/tb';
import CanopySelect from '../../canopy-ui-components/components/CanopySelect/CanopySelect';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';

export const ItemTypes = {
    MENU: 'MENU',
};

interface DragItem {
    index: number;
    id: string;
    type: string;
    menu: any;
    level: number;
}
const DeviceIconMap: any = {
    desktop: {
        on: <MdDesktopWindows />,
        off: <MdOutlineDesktopAccessDisabled />,
    },
    tablet: {
        on: <TbDeviceTablet />,
        off: <TbDeviceTabletOff />,
    },
    mobile: { on: <TbDeviceMobile />, off: <TbDeviceMobileOff /> },
    'micro-fe': { on: <TbBrowserCheck />, off: <TbBrowserOff /> },
};
const formatOptionLabel = ({ label, description }: any) => (
    <Box>
        <Box>{label}</Box>
        <Box color={'#AAA'}>{description}</Box>
    </Box>
);
const DEVICE_TYPE = ['desktop', 'tablet', 'mobile', 'micro-fe'];

const canMove = ({ previewRef, dragIndex, hoverIndex, monitor }: any) => {
    // Don't replace items with themselves
    if (dragIndex === hoverIndex) {
        return false;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = previewRef.current?.getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return false;
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return false;
    }

    return true;
};

const Menu = ({
    titleTranslation,
    app,
    menu,
    edit,
    totalDepth,
    level,
    updateOptions,
    options,
    updateMenu,
    submenuOptions,
    disabled,
    ...props
}: any) => {
    const currentColors = useCurrentColors();
    const [subMenus, setSubMenus] = useState<any>([]);

    useEffect(() => {
        setSubMenus(menu?.sub_menu || menu?.subMenus || []);
    }, [menu]);

    const handleUpdateMenu = useCallback(
        (selectedMenu: any) => {
            const originalMenu = options.find(
                (o: any) => o.key === selectedMenu.value
            );

            updateMenu(props.index, {
                ...originalMenu,
                sub_menu: subMenus,
            });
            updateOptions(originalMenu, 'REMOVE', menu);
        },
        [menu, options, updateMenu, subMenus, updateOptions, props.index]
    );

    const handleDeviceSelection = useCallback(
        (e: any) => {
            const selectedValue = e?.target?.value;
            let excludedOn = menu.excluded_on || [];
            if (!e.target.checked) {
                excludedOn = excludedOn.filter(
                    (val: string) => val !== selectedValue
                );
            } else {
                excludedOn = excludedOn.concat(selectedValue);
            }
            updateMenu(props.index, {
                ...menu,
                excluded_on: [...excludedOn],
            });
        },
        [menu, updateOptions, props.index]
    );

    const addMenu = useCallback(
        (event: any) => {
            event.stopPropagation();
            if (edit) {
                const list = [...subMenus, {}];
                setSubMenus(list);
                updateMenu(props.index, {
                    ...menu,
                    sub_menu: list,
                });
            }
        },
        [subMenus, edit, menu, updateMenu]
    );

    const deleteMenu = useCallback(
        (index: number, item: any) => {
            if (edit) {
                if (index === null) {
                    index = subMenus.findIndex((m: any) => m.key === item.key);
                }
                if (index > -1) {
                    const updatedMenu = subMenus.filter(
                        (m: any, i: number) => index !== i
                    );
                    setSubMenus(updatedMenu);
                    updateMenu(index > 0 ? index - 1 : index, {
                        ...menu,
                        sub_menu: updatedMenu,
                    });
                    updateOptions(item, 'ADD');
                } else {
                    props.deleteMenu(props.index, item);
                }
            }
        },
        [props.deleteMenu, edit, menu, subMenus, updateMenu, updateOptions]
    );

    const handleDeleteMenu = useCallback(
        (event: any) => {
            event.stopPropagation();
            if (edit) {
                props.deleteMenu(props.index, menu);
            }
        },
        [props.deleteMenu, edit, menu]
    );

    const updateSubMenu = useCallback(
        (index: number, subMenu: any) => {
            let updatedMenu = cloneDeep(subMenus);
            if (index === null) {
                updatedMenu = { ...updatedMenu[index], ...subMenu };
            } else {
                updatedMenu[index] = { ...updatedMenu[index], ...subMenu };
            }
            setSubMenus(updatedMenu);

            props.addSubMenu(menu, updatedMenu);
        },
        [subMenus, props.addSubMenu]
    );

    const addSubMenu = useCallback(
        (subMenu: any, list: any) => {
            const index = subMenus.findIndex((m: any) => m.key === subMenu.key);

            const updatedMenu = cloneDeep(subMenus);
            updatedMenu[index] = { ...updatedMenu[index], sub_menu: list };
            setSubMenus(updatedMenu);
            props.addSubMenu(menu, updatedMenu);
        },
        [subMenus, props.addSubMenu]
    );

    const moveCard = useCallback(
        (
            dragIndex: number /** source */,
            hoverIndex: number /** destination */,
            item: any
        ) => {
            const source = subMenus.findIndex(
                (m: any) => m.key === item.menu.key
            );
            if (source > -1) {
                const destination = hoverIndex;

                if (destination > -1) {
                    const list = cloneDeep(subMenus);
                    const sourceItem = list[source];

                    if (destination < source) {
                        list.splice(destination, 0, sourceItem);
                        list.splice(source + 1, 1);
                    } else if (destination > source) {
                        list.splice(destination + 1, 0, sourceItem);
                        list.splice(source, 1);
                    }
                    setSubMenus(list);
                    props.addSubMenu(menu, list);
                }
            } else {
                props.moveCard(dragIndex, hoverIndex, item);
            }
        },
        [subMenus, menu, props.addSubMenu, props.moveCard, props.index]
    );

    const dragRef = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop<
        DragItem,
        void,
        { handlerId: Identifier | null }
    >({
        accept: ItemTypes.MENU,
        collect(monitor) {
            if (previewRef.current) {
                previewRef.current.style.borderTop = '0';
            }
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        drop(item: DragItem, monitor) {
            if (!previewRef.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = props.index;

            const move = canMove({
                previewRef,
                dragIndex,
                hoverIndex,
                monitor,
            });

            if (move) {
                // Time to actually perform the action
                moveCard(dragIndex, hoverIndex, item);

                previewRef.current.style.borderTop = '0';

                // Note: we're mutating the monitor item here!
                // Generally it's better to avoid mutations,
                // but it's good here for the sake of performance
                // to avoid expensive index searches.
                item.index = hoverIndex;
            }
        },
        hover(item: DragItem, monitor) {
            if (!previewRef.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = props.index;

            const move = canMove({
                previewRef,
                dragIndex,
                hoverIndex,
                monitor,
            });

            if (move) {
                const dataset = previewRef.current.dataset;
                const dataLevel = dataset?.level;
                if (dataLevel !== undefined && item.level === +dataLevel) {
                    previewRef.current.style.borderTop = `2px dashed ${props.currentColors.brand['600']}`;
                }
            }
        },
    });

    const [{ isDragging }, drag, preview] = useDrag(
        {
            type: ItemTypes.MENU,
            item: () => {
                return {
                    menu,
                    pre: previewRef.current,
                    index: props.index,
                    level,
                };
            },
            collect: (monitor: any) => ({
                isDragging: monitor.isDragging(),
            }),
        },
        [previewRef, menu, props.index, level]
    );

    if (edit) {
        drag(dragRef);
        drop(preview(previewRef));
    }
    const onDeviceToggle = (device: string) => {
        if (menu?.excluded_on?.includes(device)) {
            updateMenu(props.index, {
                ...menu,
                excluded_on: menu?.excluded_on?.filter(
                    (item: string) => item !== device
                ),
            });
        } else {
            updateMenu(props.index, {
                ...menu,
                excluded_on: [...(menu.excluded_on || []), device],
            });
        }
    };
    return (
        <Box
            opacity={isDragging ? 0.2 : 1}
            py={2}
            ref={previewRef}
            data-level={level}
            data-label={menu.key}
            data-handler-id={handlerId}
        >
            <Box
                data-group
                display="flex"
                alignItems="center"
            >
                {edit && (
                    <Box
                        ref={dragRef}
                        display="flex"
                        cursor="pointer"
                        alignItems="center"
                        opacity={0}
                        _groupHover={{
                            opacity: 1,
                        }}
                    >
                        <MdDragIndicator
                            fontSize="24px"
                            color={props.currentColors.whiteAlpha['400']}
                        />
                    </Box>
                )}
                <Flex
                    paddingY={props.paddingY}
                    px={props.px}
                    borderRadius={props.borderRadius}
                    borderWidth={props.borderWidth}
                    borderColor={props.borderColor}
                    backgroundColor={props.backgroundColor}
                    position="relative"
                    mb={0}
                    flex={1}
                    justifyContent="space-between"
                >
                    {!edit ? (
                        <>
                            {props.t(`${titleTranslation}${menu?.key}`)}{' '}
                            {(app === 'micro-fe'
                                ? DEVICE_TYPE.filter((e) => e !== 'micro-fe')
                                : DEVICE_TYPE
                            )?.map((device) => {
                                return menu?.excluded_on?.includes(device) ? (
                                    <Tooltip
                                        key={device}
                                        aria-label={`excluded-on-${device}`}
                                        label={props.t(
                                            `ui.mm.tooltip.excluded-on-device`,
                                            { device }
                                        )}
                                        hasArrow
                                    >
                                        <IconButton
                                            background="transparent"
                                            border="none"
                                            color={
                                                currentColors.whiteAlpha['400']
                                            }
                                            icon={DeviceIconMap[device]['off']}
                                            aria-label={`excluded-on-${device}`}
                                            variant="ghost"
                                            _hover={{
                                                backgroundColor: 'transparent',
                                            }}
                                            _focus={{
                                                border: '0px',
                                                backgroundColor: 'transparent',
                                                outline: 'none',
                                            }}
                                            _active={{ border: '0px' }}
                                        />
                                    </Tooltip>
                                ) : null;
                            })}
                        </>
                    ) : (
                        <HStack spacing="15px">
                            <CanopySelect
                                value={
                                    menu?.key && {
                                        value: menu.key,
                                        label: props.t(
                                            `${titleTranslation}${menu.key}`
                                        ),
                                    }
                                }
                                options={options.map((option: any) => ({
                                    value: option?.key,
                                    label: props.t(
                                        `${titleTranslation}${option?.key}`
                                    ),
                                    description: option?.description,
                                }))}
                                formatOptionLabel={formatOptionLabel}
                                placeholder={props.t('select')}
                                onChange={handleUpdateMenu}
                                width={'2xs'}
                                height="35px"
                            ></CanopySelect>
                            <>
                                {(app === 'micro-fe'
                                    ? DEVICE_TYPE.filter(
                                          (e) => e !== 'micro-fe'
                                      )
                                    : DEVICE_TYPE
                                )?.map((device) => {
                                    const term = menu?.excluded_on?.includes(
                                        device
                                    )
                                        ? 'include'
                                        : 'exclude';
                                    return (
                                        <Tooltip
                                            key={device}
                                            aria-label={`${term}-on-${device}`}
                                            label={props.t(
                                                `ui.mm.tooltip.${term}-on-device`,
                                                { device }
                                            )}
                                            hasArrow
                                        >
                                            <IconButton
                                                background="transparent"
                                                cursor="pointer"
                                                border="none"
                                                color={
                                                    currentColors.whiteAlpha[
                                                        '400'
                                                    ]
                                                }
                                                icon={
                                                    menu?.excluded_on?.includes(
                                                        device
                                                    )
                                                        ? DeviceIconMap[device][
                                                              'off'
                                                          ]
                                                        : DeviceIconMap[device][
                                                              'on'
                                                          ]
                                                }
                                                aria-label={`${term}-on-${device}`}
                                                onClick={() => {
                                                    onDeviceToggle(device);
                                                }}
                                                variant="ghost"
                                                _hover={{
                                                    backgroundColor:
                                                        'transparent',
                                                }}
                                                _focus={{
                                                    border: '0px',
                                                    backgroundColor:
                                                        'transparent',
                                                    outline: 'none',
                                                }}
                                                _active={{ border: '0px' }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </>
                            {/* <CanopyMultiOptionDropDown
                                placeholder="ui.mm.excluded-on"
                                onOptionSelection={handleDeviceSelection}
                                selectedOptions={menu?.excluded_on}
                                availableOptions={DEVICE_TYPE?.map((item) => ({
                                    key: item,
                                    value: item,
                                }))}></CanopyMultiOptionDropDown> */}

                            <Box display="flex">
                                {level < totalDepth && (
                                    <Tooltip
                                        aria-label="add-menu"
                                        label={props.t(
                                            'ui.mm.tooltip.add-menu'
                                        )}
                                        hasArrow
                                    >
                                        <IconButton
                                            background="transparent"
                                            cursor="pointer"
                                            border="none"
                                            mr={2}
                                            icon={
                                                <AddIcon
                                                    width={'14px !important'}
                                                    height={'14px !important'}
                                                />
                                            }
                                            aria-label="add-menu"
                                            variant="ghost"
                                            opacity={0}
                                            _groupHover={{
                                                opacity: 1,
                                            }}
                                            color={
                                                props.currentColors.brand['500']
                                            }
                                            _hover={{
                                                backgroundColor: 'transparent',
                                                color: props.currentColors
                                                    .brand['400'],
                                            }}
                                            _focus={{
                                                border: '0px',
                                                backgroundColor: 'transparent',
                                                outline: 'none',
                                            }}
                                            _active={{ border: '0px' }}
                                            onClick={addMenu}
                                            disabled={disabled || !menu?.key}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip
                                    aria-label="delete-menu"
                                    label={props.t('ui.mm.tooltip.delete-menu')}
                                    hasArrow
                                >
                                    <IconButton
                                        icon={
                                            <DeleteIcon
                                                width={'14px !important'}
                                                height={'14px !important'}
                                            />
                                        }
                                        background="transparent"
                                        cursor="pointer"
                                        border="none"
                                        mr={2}
                                        aria-label="delete-menu"
                                        variant="ghost"
                                        color={props.currentColors.brand['500']}
                                        _hover={{
                                            backgroundColor: 'transparent',
                                            color: props.currentColors.brand[
                                                '400'
                                            ],
                                        }}
                                        opacity={0}
                                        _groupHover={{
                                            opacity: 1,
                                        }}
                                        _focus={{
                                            border: '0px',
                                            backgroundColor: 'transparent',
                                            outline: 'none',
                                        }}
                                        _active={{ border: '0px' }}
                                        onClick={handleDeleteMenu}
                                        disabled={disabled}
                                    />
                                </Tooltip>
                            </Box>
                        </HStack>
                    )}
                </Flex>
            </Box>
            {subMenus?.length ? (
                <Box
                    pl={8}
                    mt={4}
                >
                    {subMenus?.map((submenu: any, index: number) => {
                        return (
                            <Menu
                                {...props}
                                index={index}
                                titleTranslation="ui.mm.sub-menu-"
                                key={`${submenu?.key}_${level}_${index}`}
                                menu={submenu}
                                paddingY={2}
                                px={4}
                                borderRadius={4}
                                borderWidth={1}
                                borderColor={
                                    props.currentColors.componentBorder
                                }
                                edit={edit}
                                level={level + 1}
                                totalDepth={totalDepth}
                                options={submenuOptions}
                                submenuOptions={submenuOptions}
                                addSubMenu={addSubMenu}
                                moveCard={moveCard}
                                updateMenu={updateSubMenu}
                                deleteMenu={deleteMenu}
                                updateOptions={updateOptions}
                            ></Menu>
                        );
                    })}
                </Box>
            ) : null}
        </Box>
    );
};

export default Menu;
